import React, { useState, useRef, useEffect } from 'react';
import { Box, Grid, Typography, useMediaQuery } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Doughnut } from 'react-chartjs-2';
import Image from 'next/image';
import {
  FormPublicDesignFragment,
  FormPublicPageFragment,
} from '@/graphql/fragment/form.public.fragment';
import { SubmissionPublicFieldFragment } from '@/graphql/fragment/submission.public.fragment';
import {
  ArticleContainer,
  ArticleLinkButton,
  ArticleText,
  IconCircle,
  IconWrapper,
  ModelName,
  PieCenterLabel,
  PieChartsRow,
  PieWrapper,
  SubTitle,
  TooltipTextContainer,
  ToolTipSubTitle,
  ToolTipText,
  ToolTipLinkButton,
  TooltipContent,
  TooltipIconAndWrapper,
  TooltipCloseButton,
} from './InnovationTool.css';
import {
  arcColors,
  calculateModelPercentages,
  getDominantModel,
  getDominantModelText,
  getModelTooltipData,
  getValues,
  Models,
  NEXT_STEPS,
  RESULTS,
} from './utils';
import orb from './assets/orb.svg';

// Chart.js registration
import { Chart, ArcElement, Tooltip as ChartTooltip, Legend } from 'chart.js';
Chart.register(ArcElement, ChartTooltip, Legend);

interface Props {
  page: FormPublicPageFragment;
  design: FormPublicDesignFragment;
  answers: SubmissionPublicFieldFragment[];
}

export const EndPage: React.FC<Props> = ({ page, design, answers }) => {
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const labelRefs = useRef<Record<string, HTMLSpanElement | null>>({});
  const [arrowLeft, setArrowLeft] = useState<number | null>();
  const [arrowTop, setArrowTop] = useState<number | null>();
  const isMobile = useMediaQuery('(max-width:600px)');
  const selectedLabelRef = selectedModel
    ? labelRefs.current[selectedModel]
    : null;
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (selectedModel && selectedLabelRef) {
      const rect = selectedLabelRef.getBoundingClientRect();
      setArrowLeft(rect.left + rect.width / 2);
      setArrowTop(window.scrollY + rect.bottom + 8);
    }
  }, [selectedModel, selectedLabelRef]);

  useEffect(() => {
    if (isMobile && selectedModel && tooltipRef.current) {
      tooltipRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isMobile, selectedModel]);

  if (!page.show) return null;

  const values = getValues(answers);
  const percentages = calculateModelPercentages(values);
  const tooltipData = getModelTooltipData(selectedModel ?? '');
  const dominantModel = getDominantModel(percentages);
  const dominantModelText = getDominantModelText(dominantModel);

  return (
    <Grid
      sx={{
        marginTop: '113px',
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'center',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 'calc(100vh - 113px)',
        padding: ['0 24px', '0 24px', '0 20vw'],
        overflowY: 'auto',
        overflowX: 'hidden',
        backgroundImage: `url(${orb})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'bottom right',
        backgroundSize: '800px 404px',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'start', width: '100%' }}>
        <Typography
          sx={{
            typography: { xs: 'subtitle2' },
            paddingTop: { xs: '10px', sm: 0 },
          }}
          style={{
            color: design.colors.question,
            fontWeight: 800,
            fontStyle: 'italic',
            letterSpacing: '4.56px',
          }}
        >
          {RESULTS.toUpperCase()}
        </Typography>
      </Box>
      <Box>
        <Box
          sx={{
            color: design.colors.question,
            fontSize: { xs: '18px', sm: '24px' },
            textAlign: 'left',
          }}
        >
          {dominantModelText.title}
          <SubTitle>
            {dominantModelText.context_one}{' '}
            <span style={{ fontWeight: 'normal' }}>
              {dominantModelText.context_two}
            </span>
          </SubTitle>
        </Box>
      </Box>
      <Box
        sx={{
          color: design.colors.question,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <PieChartsRow>
          {Object.entries(percentages).map(([label, value]) => {
            const color = arcColors[label as keyof typeof Models];
            const isDominant = dominantModel === label;
            const doughnutData = {
              labels: [label, 'Other'],
              datasets: [
                {
                  data: [value, 100 - value],
                  backgroundColor: [color, '#CFCFCF'],
                  borderWidth: 0,
                },
              ],
            };
            const doughnutOptions = {
              cutout: '88%',
              plugins: {
                legend: { display: false },
                tooltip: { enabled: false },
              },
              maintainAspectRatio: false,
            };
            return (
              <PieWrapper key={label}>
                <div style={{ position: 'relative', width: 150, height: 150 }}>
                  <Doughnut
                    data={doughnutData}
                    options={doughnutOptions}
                    width={150}
                    height={150}
                  />
                  <PieCenterLabel
                    style={{
                      color: isDominant ? color : '#eaeaea',
                    }}
                  >
                    {`${value}%`}
                  </PieCenterLabel>
                </div>
                <span
                  ref={(el) => {
                    labelRefs.current[label] = el;
                  }}
                  style={{
                    cursor: 'pointer',
                    fontWeight: isDominant ? 'bold' : 'normal',
                    position: 'relative',
                    display: 'inline-block',
                  }}
                  onClick={() => setSelectedModel(label)}
                >
                  <ModelName>{label}</ModelName>
                </span>

                {/* Render the arrow below the selected ModelName */}
                {!isMobile && selectedModel && (
                  <div
                    style={{
                      position: 'fixed',
                      top: arrowTop ? `${arrowTop}px` : undefined,
                      left: arrowLeft
                        ? `calc(${arrowLeft}px - 20px)`
                        : undefined,
                      zIndex: 1001,
                      pointerEvents: 'none',
                      width: '40px',
                      height: '24px',
                    }}
                  >
                    <svg width='40' height='24' viewBox='0 0 40 24'>
                      <polygon points='20,0 0,24 40,24' fill='#fff' />
                    </svg>
                  </div>
                )}
              </PieWrapper>
            );
          })}
        </PieChartsRow>

        {selectedModel && arrowLeft !== null && arrowTop !== null && (
          <TooltipContent ref={tooltipRef}>
            <TooltipCloseButton
              aria-label='Close'
              onClick={() => setSelectedModel(null)}
            >
              <CloseIcon style={{ fontSize: 22 }} />
            </TooltipCloseButton>
            <TooltipIconAndWrapper>
              <IconWrapper>
                <IconCircle color={tooltipData.color}>
                  <Image
                    src={tooltipData.icon}
                    alt='icon'
                    width={isMobile ? 90 : 166}
                    height={isMobile ? 90 : 166}
                    style={{
                      objectFit: 'contain',
                      width: isMobile ? '40px' : '120px',
                      height: isMobile ? '40px' : '120px',
                    }}
                  />
                </IconCircle>
              </IconWrapper>
              <TooltipTextContainer>
                <ToolTipSubTitle>
                  <span style={{ fontWeight: 'bold' }}>
                    {tooltipData.title}
                  </span>{' '}
                  {tooltipData.subtitle}
                </ToolTipSubTitle>
                <ToolTipText
                  style={{
                    display: 'block',
                    marginTop: '16px',
                    marginBottom: '5px',
                    padding: 0,
                  }}
                >
                  {NEXT_STEPS}
                </ToolTipText>
                {tooltipData.items.map((item, index) =>
                  index === tooltipData.items.length ? (
                    <ToolTipLinkButton key={index}>• {item}</ToolTipLinkButton>
                  ) : (
                    <ToolTipText
                      key={index}
                      style={{
                        display: 'block',
                        margin: 0,
                        padding: '0 0 0 10px',
                      }}
                    >
                      {`• ${item}`}
                    </ToolTipText>
                  )
                )}
              </TooltipTextContainer>
            </TooltipIconAndWrapper>
          </TooltipContent>
        )}
      </Box>
    </Grid>
  );
};
